import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Tag } from "@phosphor-icons/react";

export default function PriceTablesPage() {
  const [tables, setTables] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", prazos_text: "", max_desconto_pct: "", active: true });

  const load = async () => setTables((await api.get("/price-tables")).data);
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: "", prazos_text: "", max_desconto_pct: "", active: true }); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ name: t.name, prazos_text: (t.prazos_permitidos || []).join("\n"), max_desconto_pct: t.max_desconto_pct ?? "", active: t.active }); setOpen(true); };

  const save = async () => {
    try {
      const prazos = form.prazos_text.split("\n").map((s) => s.trim()).filter(Boolean);
      const payload = {
        name: form.name,
        prazos_permitidos: prazos,
        max_desconto_pct: form.max_desconto_pct === "" ? null : Number(form.max_desconto_pct),
        active: form.active,
      };
      if (editing) await api.put(`/price-tables/${editing.id}`, payload);
      else await api.post("/price-tables", payload);
      toast.success("Tabela salva."); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (t) => { if (!window.confirm(`Excluir tabela ${t.name}?`)) return; await api.delete(`/price-tables/${t.id}`); toast.success("Excluída."); load(); };

  return (
    <div className="space-y-6" data-testid="price-tables-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950">Tabelas de Preço</h1>
          <p className="text-muted-foreground mt-1">Defina prazos máximos por tabela (ex: 30 DIAS).</p>
        </div>
        <Button onClick={openNew} className="bg-accent hover:bg-accent/90 text-white gap-2" data-testid="new-table-btn"><Plus size={18} weight="bold" /> Nova Tabela</Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {tables.length === 0 && <p className="text-muted-foreground">Nenhuma tabela cadastrada.</p>}
        {tables.map((t) => (
          <Card key={t.id} className="p-5 shadow-none border-border" data-testid={`table-card-${t.id}`}>
            <div className="flex items-start justify-between">
              <div className="h-10 w-10 rounded-md bg-accent/10 text-accent flex items-center justify-center"><Tag size={20} weight="bold" /></div>
              <div className="flex gap-2">
                <button onClick={() => openEdit(t)} className="text-zinc-400 hover:text-accent" data-testid={`edit-table-${t.id}`}><PencilSimple size={18} /></button>
                <button onClick={() => remove(t)} className="text-zinc-400 hover:text-destructive"><Trash size={18} /></button>
              </div>
            </div>
            <h3 className="font-heading font-semibold text-lg text-zinc-900 mt-3">{t.name}</h3>
            <p className="text-sm text-muted-foreground">{(t.prazos_permitidos || []).length} condições de pagamento</p>
            <p className="text-xs text-muted-foreground mt-0.5">Desconto máx: {t.max_desconto_pct != null ? `${t.max_desconto_pct}%` : "padrão"}</p>
            {(t.prazos_permitidos || []).length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {(t.prazos_permitidos || []).slice(0, 6).map((p) => <span key={p} className="text-[11px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600">{p}</span>)}
              </div>
            )}
            <Badge variant="outline" className={`mt-2 ${t.active ? "border-accent text-accent" : "border-zinc-300 text-zinc-400"}`}>{t.active ? "Ativa" : "Inativa"}</Badge>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Editar tabela" : "Nova tabela"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1"><Label className="text-xs uppercase text-zinc-500">Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: 30 DIAS" data-testid="table-name" /></div>
            <div className="space-y-1">
              <Label className="text-xs uppercase text-zinc-500">Condições de pagamento (uma por linha)</Label>
              <Textarea value={form.prazos_text} onChange={(e) => setForm({ ...form, prazos_text: e.target.value })} rows={6} placeholder={"A VISTA\n30 DIAS\n20/30/40\n15/30/45"} data-testid="table-prazos" />
              <p className="text-xs text-muted-foreground">Estas são as opções que o representante poderá escolher no pedido.</p>
            </div>
            <div className="space-y-1"><Label className="text-xs uppercase text-zinc-500">Desconto máximo (%) — opcional</Label><Input type="number" value={form.max_desconto_pct} onChange={(e) => setForm({ ...form, max_desconto_pct: e.target.value })} placeholder="Vazio = usa o limite global" data-testid="table-maxdesc" /></div>
            <div className="flex items-center gap-3"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label className="text-sm">Tabela ativa</Label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-accent hover:bg-accent/90 text-white" data-testid="save-table-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
